import api from './api';

// ===================================================================
// Typen (alle Felder optional/nullable - defensives Parsing!)
// Backend: generisches Rating-System, listingType 'setup'
// ===================================================================

export interface SetupRating {
  id?: string | null;
  listingType?: string | null;
  listingId?: string | null;
  stars: number;
  comment?: string | null;
  userId?: string | null;
  userName?: string | null;
  userImage?: string | null;
  createdAt?: number | null;
  updatedAt?: number | null;
  [key: string]: any;
}

export interface RatingSummary {
  average: number;
  count: number;
}

/** Sterne-Verteilung: Index 5..1 → Anzahl */
export interface StarDistribution {
  stars: 5 | 4 | 3 | 2 | 1;
  count: number;
  percent: number;
}

// ===================================================================
// Defensive Parser
// ===================================================================

function clampStars(value: any): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, n));
}

function parseRating(data: any): SetupRating | null {
  if (!data || typeof data !== 'object') return null;
  const stars = clampStars(data.stars);
  if (stars <= 0) return null;
  return {
    ...data,
    id: data.id ?? data._id ?? null,
    listingType: data.listingType ?? null,
    listingId: data.listingId ?? null,
    stars,
    comment: typeof data.comment === 'string' && data.comment.trim().length > 0 ? data.comment : null,
    userId: data.userId ?? null,
    userName: data.userName ?? null,
    userImage: data.userImage ?? null,
    createdAt: typeof data.createdAt === 'number' ? data.createdAt : null,
    updatedAt: typeof data.updatedAt === 'number' ? data.updatedAt : null,
  };
}

// ===================================================================
// Service
// ===================================================================

class SetupRatingService {
  /**
   * GET /api/ratings/setup/:setupId/summary
   * Liefert Durchschnitt & Anzahl. Bei Fehlern { average: 0, count: 0 }.
   */
  async getSummary(setupId: string): Promise<RatingSummary> {
    try {
      const res = await api.get(
        `/api/ratings/setup/${encodeURIComponent(setupId)}/summary`
      );
      const source = res?.data?.data ?? res?.data ?? {};
      const average = typeof source?.average === 'number' && Number.isFinite(source.average)
        ? source.average
        : 0;
      const count = typeof source?.count === 'number' && source.count > 0 ? source.count : 0;
      return { average, count };
    } catch (_e) {
      return { average: 0, count: 0 };
    }
  }

  /**
   * GET /api/ratings/setup/:setupId
   * Liste aller Bewertungen. Antwort kann direkt ein Array ODER
   * ein {ratings:[…]}-Wrapper sein — beides wird unterstützt.
   */
  async getRatings(setupId: string): Promise<SetupRating[]> {
    try {
      const res = await api.get(`/api/ratings/setup/${encodeURIComponent(setupId)}`);
      const payload = res?.data?.data ?? res?.data;
      const listRaw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.ratings)
          ? payload.ratings
          : [];
      return listRaw
        .map(parseRating)
        .filter((r: SetupRating | null): r is SetupRating => r !== null);
    } catch (_e) {
      return [];
    }
  }

  /**
   * GET /api/ratings/setup/:setupId/my (auth)
   * Eigenes Rating oder null (auch bei 404).
   */
  async getMyRating(setupId: string): Promise<SetupRating | null> {
    try {
      const res = await api.get(`/api/ratings/setup/${encodeURIComponent(setupId)}/my`);
      const payload = res?.data?.data ?? res?.data;
      if (!payload) return null;
      return parseRating(payload);
    } catch (_e) {
      return null;
    }
  }

  /**
   * POST /api/ratings/setup/:setupId (auth)
   * Erstellt oder aktualisiert das eigene Rating. Wirft bei Fehlern.
   */
  async submitRating(
    setupId: string,
    stars: number,
    comment?: string | null
  ): Promise<SetupRating | null> {
    const body: any = { stars };
    if (comment != null && comment.trim().length > 0) body.comment = comment.trim();
    const res = await api.post(`/api/ratings/setup/${encodeURIComponent(setupId)}`, body);
    const payload = res?.data?.data ?? res?.data;
    return parseRating(payload);
  }

  /**
   * DELETE /api/ratings/:id (auth)
   * Löscht das eigene Rating. Wirft bei Fehlern.
   */
  async deleteRating(ratingId: string): Promise<void> {
    await api.delete(`/api/ratings/${encodeURIComponent(ratingId)}`);
  }

  // =================================================================
  // Helper
  // =================================================================

  /** Berechnet die Sterne-Verteilung (5→1) aus einer Rating-Liste. */
  getStarDistribution(ratings: SetupRating[]): StarDistribution[] {
    const list = Array.isArray(ratings) ? ratings : [];
    const total = list.length;
    const buckets: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const r of list) {
      const rounded = Math.round(clampStars(r?.stars));
      if (rounded >= 1 && rounded <= 5) buckets[rounded] += 1;
    }
    return ([5, 4, 3, 2, 1] as const).map((stars) => ({
      stars,
      count: buckets[stars],
      percent: total > 0 ? Math.round((buckets[stars] / total) * 100) : 0,
    }));
  }

  /** Formatiert den Durchschnitt deutsch mit einer Nachkommastelle ("4,5"). */
  formatAverage(average?: number | null): string {
    if (typeof average !== 'number' || !Number.isFinite(average)) return '0,0';
    return average.toLocaleString('de-DE', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }

  /** Fehlermeldung aus axios-Fehler extrahieren (Backend sendet Plaintext) */
  extractErrorMessage(error: any, fallback: string): string {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (typeof data?.message === 'string' && data.message.trim().length > 0) return data.message;
    return fallback;
  }
}

const setupRatingService = new SetupRatingService();
export default setupRatingService;
